import { useEffect, useRef, useState } from "react";
import { QuerySpec } from "@src/types/dashboards";
import { getWidgetDataSource } from "./widget-query";
import { useRefetchWhenVisible } from "@src/hooks/use-check-widget-in-viewport";
import { DURATION_GAUGE_WIDGET_MS, resolvePollingMs } from "./constants";
import { getQueryTimeseriesForNodeUseCase } from "@src/cloud-connect/use-cases/get-query-timeseries-for-node";

interface UseLastWidgetValueResult {
  value: number | string | null;
  /** ISO `time` of the last value the API returned, or null when nothing
   *  is cached yet (initial load, error, cleared source, etc.). The hook
   *  just surfaces what came back; consumers pick the format themselves
   *  (e.g. the gauge uses TimezoneDate with showRelative). */
  lastTimestamp: string | null;
  /** True when the fetch succeeded but the values array was empty. */
  hasNoData: boolean;
  isLoading: boolean;
  error?: string;
  /** Latest value of each node-driven gauge bound (all null for any other
   *  widget, and for a gauge whose bounds are all static numbers). */
  boundValues: GaugeBoundValues;
}

interface QueryValue {
  time: string;
  value: number;
}

/** The gauge bounds a node can drive. Each one rides in `query.bindings` as an
 *  entry whose `field` is one of these, after the widget's own value binding. */
export const GAUGE_BOUND_FIELDS = ["min", "max", "target"] as const;
export type GaugeBoundField = (typeof GAUGE_BOUND_FIELDS)[number];

/** Fetched bound values. A null means "this bound is not node-driven", so the
 *  renderer falls back to the static number in `semantics`. */
export type GaugeBoundValues = Record<GaugeBoundField, number | null>;

const NO_BOUND_VALUES: GaugeBoundValues = {
  min: null,
  max: null,
  target: null,
};

interface NodeFetchResult {
  ok: boolean;
  value: number | string | null;
  time: string | null;
  error?: string;
}

/**
 * Fetches the latest value for a widget configured with machine+node
 * (query.source = machine, bindings[0].address = node). Raw data over a
 * relative window; each widget type passes its own duration (default: gauge's
 * 30 min) since the v3 tsdata endpoint only accepts from/to.
 */
export const useLastWidgetValue = (
  query: QuerySpec | null | undefined,
  durationMs: number = DURATION_GAUGE_WIDGET_MS,
  /** When false (off-screen widget), ticks skip fetching — the interval
   *  keeps its schedule; re-entry refetches once via the visibility bridge. */
  inViewport = true,
): UseLastWidgetValueResult => {
  const [value, setValue] = useState<number | string | null>(null);
  const [lastTimestamp, setLastTimestamp] = useState<string | null>(null);
  const [hasNoData, setHasNoData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [boundValues, setBoundValues] =
    useState<GaugeBoundValues>(NO_BOUND_VALUES);

  const dataSource = getWidgetDataSource(query);

  // Gauge bounds driven by a node, in the order they are bound. Read as a
  // single string so the effect re-runs on a re-pick without depending on a
  // fresh array every render.
  const boundNodes = GAUGE_BOUND_FIELDS.map((field) => ({
    field,
    address: query?.bindings?.find((b) => b.field === field)?.address ?? null,
  }));
  const boundNodesKey = boundNodes
    .map((bound) => `${bound.field}:${bound.address ?? ""}`)
    .join("|");
  // Read through a ref so the effect fires on `boundNodesKey` alone — the
  // array is rebuilt every render, and depending on it would re-fetch on each
  // one. Same reason `dataSource`'s fields are listed individually below.
  const boundNodesRef = useRef(boundNodes);
  boundNodesRef.current = boundNodes;

  // Visibility bridge — same role as use-widget-timeseries.
  const latestLoadRef = useRef<(() => void) | null>(null);
  const inViewportRef = useRefetchWhenVisible(inViewport, latestLoadRef);

  useEffect(() => {
    // Clear the previous widget's value as soon as the data source changes.
    // Also drop loading so a source that disappears mid-fetch can't leave the
    // widget spinning forever.
    setValue(null);
    setLastTimestamp(null);
    setHasNoData(false);
    setIsLoading(false);
    setError(undefined);
    setBoundValues(NO_BOUND_VALUES);
    if (!dataSource) {
      latestLoadRef.current = null;
      return;
    }
    const { group, source } = dataSource;
    const nodes = boundNodesRef.current;
    let cancelled = false;
    // Overlap guard: a slow endpoint must never stack parallel fetches when
    // the interval fires again mid-request.
    let inFlight = false;
    // Only the first (effect-driven) load shows the spinner; background
    // refreshes replace the value in place so the widget never flickers.
    let isInitialLoad = true;

    const load = async () => {
      if (inFlight || document.hidden || !inViewportRef.current) return;
      inFlight = true;
      if (isInitialLoad) {
        isInitialLoad = false;
        setIsLoading(true);
      }
      try {
        const to = new Date().toISOString();
        const from = new Date(Date.now() - durationMs).toISOString();
        const fetchNode = async (node: string): Promise<NodeFetchResult> => {
          try {
            const result = await getQueryTimeseriesForNodeUseCase({
              group,
              source: node,
              frequency: "raw",
              from,
              to,
            });
            if (!result.success) {
              return {
                ok: false,
                value: null,
                time: null,
                error: result.error,
              };
            }
            const values = (
              result.data as { values?: QueryValue[] } | undefined
            )?.values;
            const last = values?.[values.length - 1];
            const raw = last?.value as unknown;
            // Values may be numeric (gauge target) or string (status
            // nodes bound by single-text-value). Strings are accepted
            // verbatim; numbers stay as numbers. Anything else (boolean /
            // nested) drops to null so the gauge's bound logic and the
            // single-text-value renderer both have a sane value to draw.
            const accepted: number | string | null =
              typeof raw === "number" || typeof raw === "string"
                ? (raw as number | string)
                : null;
            return {
              ok: true,
              value: last ? accepted : null,
              time: last?.time ?? null,
            };
          } catch (e) {
            return { ok: false, value: null, time: null, error: String(e) };
          }
        };

        // One request per node, ALL IN FLIGHT TOGETHER: nothing is drawn until
        // every required value has landed, so running them in series would only
        // delay the first paint.
        const [valueResult, boundResults] = await Promise.all([
          fetchNode(source),
          Promise.all(
            nodes.map((bound) =>
              bound.address == null ? null : fetchNode(bound.address),
            ),
          ),
        ]);
        if (cancelled) return;

        if (!valueResult.ok) {
          setError(valueResult.error);
          return;
        }
        const failedBound = boundResults.find(
          (result) => result != null && !result.ok,
        );
        if (failedBound) {
          setError(failedBound.error);
          return;
        }

        // A bound with no value is as unrenderable as a value with no bound:
        // the dial cannot be scaled against a number that never arrived.
        // Numeric-only: even though `result.value` may now be a string
        // (single-text-value lets string-types in), the gauge's dial
        // cannot scale against a non-numeric bound. Non-numeric bound
        // responses surface as null + missing so the gauge renders no
        // value rather than a broken dial.
        const nextBoundValues: GaugeBoundValues = { ...NO_BOUND_VALUES };
        let missing = valueResult.value == null;
        nodes.forEach((bound, index) => {
          const result = boundResults[index];
          if (result == null) return;
          const numericBound =
            typeof result.value === "number" ? result.value : null;
          nextBoundValues[bound.field] = numericBound;
          if (numericBound == null) missing = true;
        });

        setError(undefined);
        setHasNoData(missing);
        setBoundValues(missing ? NO_BOUND_VALUES : nextBoundValues);
        setValue(missing ? null : valueResult.value);
        setLastTimestamp(missing ? null : valueResult.time);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          inFlight = false;
        }
      }
    };

    // Expose the latest fetch to the visibility bridge (re-entry refetch).
    latestLoadRef.current = load;
    load();
    // Gauges are always raw — the user-picked polling_interval drives refresh.
    const pollingMs = resolvePollingMs(
      query?.sampling?.frequency,
      query?.sampling?.polling_interval,
    );
    const intervalId =
      pollingMs != null ? setInterval(() => void load(), pollingMs) : undefined;
    return () => {
      cancelled = true;
      if (intervalId != null) clearInterval(intervalId);
      latestLoadRef.current = null;
      setIsLoading(false);
    };
  }, [
    dataSource?.group,
    dataSource?.source,
    durationMs,
    query?.sampling?.frequency,
    query?.sampling?.polling_interval,
    boundNodesKey,
  ]);

  return { value, lastTimestamp, hasNoData, isLoading, error, boundValues };
};
